// Teste de hot-reload - Alteração para verificar se as mudanças estão sendo refletidas
import React from 'react';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  AlertCircle,
  CheckCircle2
} from 'lucide-react';

// Interface for integration data
export interface IngramMicroIntegration {
  id: string;
  name: string;
  description: string;
  logo: string;
  isActive: boolean;
  credentials: {
    username?: string;
    password?: string;
    subscriptionKey?: string;
  };
  status: 'connected' | 'disconnected' | 'error';
  dbId?: string;
}

interface IngramMicroIntegrationProps {
  integration: IngramMicroIntegration;
  onToggle: (id: string) => void;
  onSave: (id: string, credentials: any) => Promise<void>;
}

const IngramMicroIntegration: React.FC<IngramMicroIntegrationProps> = ({
  integration,
  onToggle
}) => {
  const getStatusColor = () => {
    switch (integration.status) {
      case 'connected':
        return 'text-green-500';
      case 'error':
        return 'text-red-500';
      default:
        return 'text-amber-500';
    }
  };

  const getStatusText = () => {
    switch (integration.status) {
      case 'connected':
        return 'Conectado';
      case 'error':
        return 'Erro';
      default:
        return 'Desconectado';
    }
  };

  const getStatusIcon = () => {
    switch (integration.status) {
      case 'connected':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'error':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-amber-500" />;
    }
  };

  return (
    <Card className="overflow-hidden border shadow-sm">
      <CardHeader className="pb-4">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-md overflow-hidden bg-white p-1 flex items-center justify-center border shadow-sm">
              <img 
                src={integration.logo} 
                alt={`${integration.name} logo`} 
                className="h-10 w-10 object-contain"
              />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <CardTitle className="text-lg">{integration.name}</CardTitle>
                <Badge 
                  variant="outline" 
                  className={`
                    flex items-center gap-1 px-2 py-1
                    ${integration.status === 'connected' 
                      ? 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800' 
                      : integration.status === 'error'
                        ? 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800'
                        : 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800'
                    }
                  `}
                >
                  {getStatusIcon()}
                  <span>{getStatusText()}</span>
                </Badge>
              </div>
              <CardDescription className="text-sm">
                {integration.description}
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center space-x-2">
              <Switch
                id={`activate-${integration.id}`}
                checked={integration.isActive}
                onCheckedChange={() => onToggle(integration.id)}
              />
            </div>
          </div>
        </div>
      </CardHeader>
    </Card>
  );
};

export default IngramMicroIntegration; 